import { useState } from 'react';
import { 
  MessageCircle, X, Minimize2, Maximize2, 
  Send, Bot, Sparkles, HelpCircle,
  PlayCircle
} from 'lucide-react';

const AssistantPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'assistant',
      text: 'Olá! 👋 Eu sou sua assistente virtual. Estou aqui para ajudar você a dominar o Solteozap IA!',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Respostas pré-definidas do assistente
  const assistantResponses = {
    'criar agente': 'Para criar um agente, clique no botão "Contratar Agente" no topo da página. É super simples: dê um nome, descrição e instruções. Pronto! 🚀',
    'como funciona': 'O Solteozap IA permite criar funcionários digitais (agentes IA) que trabalham 24/7. Eles podem conversar via WhatsApp, Instagram, e-mail e muito mais!',
    'tutorial': 'Vou iniciar um tour guiado! Clique no botão abaixo para começar.',
    'ajuda': 'Posso ajudar com:\n• Criar agentes\n• Configurar integrações\n• Gerenciar conversas\n• Entender recursos\n\nO que você gostaria de saber?',
    'default': 'Hmm, não entendi bem. Você pode perguntar sobre:\n• Como criar agentes\n• Tutorial da plataforma\n• Integrações disponíveis\n• Gerenciamento de agentes'
  };

  const handleSendMessage = (e) => {
    e?.preventDefault();
    
    const messageText = inputMessage.trim();
    if (!messageText || isTyping) return;

    // Limpa o input imediatamente
    setInputMessage('');
    
    // Adiciona mensagem do usuário
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: messageText,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    // Simula resposta do assistente
    setTimeout(() => {
      const lowerInput = messageText.toLowerCase();
      let response = assistantResponses.default;
      
      // Busca resposta apropriada
      for (const [key, value] of Object.entries(assistantResponses)) {
        if (key !== 'default' && lowerInput.includes(key)) {
          response = value;
          break;
        }
      }

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        text: response,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 1000);
  };

  const startTutorial = () => {
    alert('Tutorial será implementado com biblioteca específica (Shepherd.js/Intro.js)');
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-20 h-20 bg-gradient-to-r from-green-800 to-green-600 rounded-full shadow-2xl hover:shadow-3xl transition-all transform hover:scale-110 flex items-center justify-center group z-50"
        data-tutorial="assistant-button"
      >
        <div className="relative">
          <MessageCircle className="w-10 h-10 text-white" />
          <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-yellow-300 animate-pulse" />
        </div>
        <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-bounce">
          1
        </span>
      </button>
    );
  }

  return (
    <div className={`fixed bottom-6 right-6 z-50 transition-all ${isMinimized ? 'w-80' : 'w-96'}`}>
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-indigo-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-800 to-green-600 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-7 h-7" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Assistente Solteozap IA</h3>
                <p className="text-sm opacity-90">Sempre online para ajudar!</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                {isMinimized ? <Maximize2 className="w-5 h-5" /> : <Minimize2 className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages Area */}
            <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.type === 'user'
                        ? 'bg-gradient-to-r from-green-800 to-green-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-800'
                    }`}
                  >
                    <p className="text-base whitespace-pre-line">{message.text}</p>
                    <p className={`text-xs mt-1 ${message.type === 'user' ? 'text-white/70' : 'text-gray-500'}`}>
                      {message.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={startTutorial}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-100 text-green-700 rounded-xl hover:bg-indigo-200 transition-colors font-medium"
                >
                  <PlayCircle className="w-4 h-4" />
                  Iniciar Tour
                </button>
                <button
                  onClick={() => {
                    setInputMessage('como criar agente');
                    // Usar setTimeout para garantir que o estado seja atualizado
                    setTimeout(() => {
                      const form = document.getElementById('assistant-form');
                      if (form) form.requestSubmit();
                    }, 0);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-colors font-medium"
                  disabled={isTyping}
                >
                  <HelpCircle className="w-4 h-4" />
                  Como criar agente?
                </button>
              </div>
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-200">
              <form 
                id="assistant-form"
                onSubmit={handleSendMessage}
                className="flex gap-2"
              >
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Digite sua dúvida..."
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 text-base"
                  disabled={isTyping}
                />
                <button
                  type="submit"
                  className="p-3 bg-gradient-to-r from-green-800 to-green-600 text-white rounded-xl hover:from-green-700 hover:to-green-700 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                  disabled={isTyping || !inputMessage.trim()}
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AssistantPopup;